/**
 * Whether a screen shows its completed items, and the control that flips it.
 *
 * A view preference, not item data (C02): it lives on the device, in
 * `localStorage` like the theme, and never reaches the store, the mirror or
 * the sync queue. Unlike the theme it is scoped to the account and to the
 * view — one key per named list, and one each for Today and Upcoming — because
 * hiding the done rows of a long shopping list says nothing about whether
 * today's page should hide what was done today. A second person signing in on
 * this device gets their own keys and finds each view at its default, never at
 * what someone else left it on. A list and Today start with everything shown:
 * a filter nobody chose is a list that looks shorter than it is. Upcoming
 * starts with completed items hidden (g91), because it is a look ahead and
 * what is done is not ahead.
 *
 * The rules a screen has to keep, from C01–C05, live here so the named list
 * and the two projections cannot drift apart: the label, the count, the
 * messages, whether the control is offered at all (g52), and the one way to
 * apply a toggle over open drafts.
 */

import { el } from "../lib/dom.js";
import { icon } from "../lib/icons.js";
import type { DetailHost, EditorGroup } from "./ui.js";

export type CompletedView = "list" | "today" | "upcoming";

export interface CompletedScope {
  userId: string;
  view: CompletedView;
  /** A named list's id; absent for the projections. */
  listId?: string;
}

export const COMPLETED_PREFIX = "trayflux.completed";

export function completedKey(scope: CompletedScope): string {
  const base = `${COMPLETED_PREFIX}.${scope.userId}.${scope.view}`;
  return scope.listId === undefined ? base : `${base}.${scope.listId}`;
}

type Stored = "show" | "hide";

/**
 * What a view does while the device holds no choice for it. A stored `show`
 * or `hide` always wins, so a device that chose to show Upcoming's completed
 * items before g91 still shows them.
 */
const HIDDEN_BY_DEFAULT: Record<CompletedView, boolean> = {
  list: false,
  today: false,
  upcoming: true,
};

function isStored(value: unknown): value is Stored {
  return value === "show" || value === "hide";
}

export interface CompletedVisibility {
  /** True while completed items are filtered out of the view. */
  readonly hidden: boolean;
  /**
   * True once the person has chosen: a stored choice was found, or `set` has
   * run. False while `hidden` is only the view's default.
   */
  readonly chosen: boolean;
  set(hidden: boolean): void;
}

/**
 * The stored choice for one scope, defaulting to the view's own default:
 * showing (C02), except in Upcoming, which hides (g91).
 *
 * `hidden` is a getter rather than a field so a screen that reads it after
 * `set` is never looking at a stale copy. Every storage access is wrapped: a
 * private window does not hand back null, the accessor itself throws, and a
 * view preference must not be able to stop a screen drawing. When storage
 * refuses the write the choice still moves in memory — the person's tap
 * worked; it just will not survive a reload — which is the theme's rule too.
 */
export function completedVisibility(scope: CompletedScope): CompletedVisibility {
  const key = completedKey(scope);
  let hidden = HIDDEN_BY_DEFAULT[scope.view];
  let chosen = false;
  try {
    const value = localStorage.getItem(key);
    if (isStored(value)) {
      hidden = value === "hide";
      chosen = true;
    }
  } catch {
    // The view's default, above.
  }
  return {
    get hidden() {
      return hidden;
    },
    get chosen() {
      return chosen;
    },
    set(next) {
      hidden = next;
      chosen = true;
      try {
        localStorage.setItem(key, next ? "hide" : "show");
      } catch {
        // Kept in memory for this visit; see above.
      }
    },
  };
}

/** "3 completed items", "1 completed item". */
export function completedItems(count: number): string {
  return `${count} completed ${count === 1 ? "item" : "items"}`;
}

/**
 * The control's text, which is also its accessible name (C01): it describes
 * the next action and carries the count, and there is no pressed state to
 * disagree with it.
 */
export function completedLabel(hidden: boolean, count: number): string {
  return `${hidden ? "Show" : "Hide"} completed · ${count}`;
}

/**
 * C05, in place of the empty-list invitation. Names the header's control as
 * the way back (g56) rather than adding a second one here, and says the items
 * are hidden, never gone.
 */
export function allDoneMessage(count: number): string {
  return `All done. ${completedItems(count)} ${count === 1 ? "is" : "are"} hidden. Choose Show completed to see them.`;
}

/** Announced when the filter goes on. */
export function hiddenCountMessage(count: number): string {
  return `${completedItems(count)} hidden.`;
}

/** Announced when the filter comes off. */
export const SHOWING_COMPLETED = "Showing completed items.";

/** Announced when a tick takes a row out of a filtered view (C04). */
export function tickedHiddenMessage(text: string, count: number): string {
  return `${text} done and hidden. ${hiddenCountMessage(count)}`;
}

/** Shown and announced when a deep link reveals a row the filter would hide. */
export function revealedMessage(text: string): string {
  return `${text} is completed and hidden. Shown until you leave.`;
}

/**
 * Whether the control is offered (g52). "Hide completed · 0" is an action with
 * nothing to act on, and it cost a phone's header a whole line, so an
 * inactive control at zero is left out. An active filter always stays, even
 * at zero: "Show completed · 0" is what tells a person their view is
 * filtered, and the only way to turn the filter off.
 *
 * That holds for a filter the person chose. Upcoming's filter is on before
 * anyone chooses it (g91), and at zero it is hiding nothing: until there is
 * a choice or something completed, the control is left out there too, or
 * every Upcoming header would carry "Show completed · 0".
 */
export function completedOffered(hidden: boolean, count: number, chosen = true): boolean {
  return (hidden && chosen) || count > 0;
}

export interface CompletedControl {
  node: HTMLButtonElement;
  paint(hidden: boolean, count: number, chosen?: boolean): void;
}

/**
 * The eye button. A regular action button: its text names the next action
 * and the count, and that text is its name — no `aria-label` to fall out of
 * step with it, and no `aria-pressed`, which would read "Hide completed,
 * pressed" for a control that is about to show them. The `filtering` class
 * is the accent-soft treatment while the filter is on.
 *
 * It leaves the header when `completedOffered` says so, but never from under
 * the focus. Pressing "Show completed · 0" turns the filter off at zero, and
 * another device can take the last completed item away while a keyboard
 * user is on the control; removing it then would drop the focus on the page
 * body. So it stays, with its label up to date, until the focus leaves it,
 * and only then goes. The flag is tracked from the focus events rather than
 * read from `activeElement`, which still names the button during its own
 * `focusout`.
 */
export function completedControl(onPress: () => void): CompletedControl {
  const label = el("span", { class: "completed-toggle-text" });
  let offered = true;
  let focused = false;
  const place = () => {
    node.hidden = !offered && !focused;
  };
  const node = el("button", {
    class: "completed-toggle",
    attrs: { type: "button" },
    children: [icon("eye"), label],
    on: {
      click: () => onPress(),
      focusin: () => { focused = true; },
      focusout: () => { focused = false; place(); },
    },
  });
  return {
    node,
    paint(hidden, count, chosen) {
      label.textContent = completedLabel(hidden, count);
      node.classList.toggle("filtering", hidden);
      offered = completedOffered(hidden, count, chosen);
      place();
    },
  };
}

/**
 * A manual toggle, over whatever drafts the screen holds (C04: resolve
 * drafts before applying a filter change that would hide their row).
 *
 * The same contract as the agenda's Show more: the open inline editor is
 * resolved first — a valid draft committed, an untouched one dropped, a blank
 * one refused, and a refusal stops the toggle with the draft still in hand.
 * Then, when the row the Details panel is showing would leave the view, the
 * panel comes down through its own guard: a changed panel asks, Keep editing
 * ends the toggle with nothing applied, Discard goes on. Only then does the
 * preference move.
 */
export function toggleCompleted(deps: {
  editors: EditorGroup;
  detail: DetailHost;
  /** True when the row the panel shows would leave the view after the toggle. */
  selectedWouldHide(): boolean;
  /** The screen's own dismissal: undoes the navigation that opened the panel. */
  dismiss(): void;
  /** Flips the preference and repaints. Reached only once every draft is settled. */
  apply(): void;
}): void {
  if (!deps.editors.resolve()) return;
  if (!deps.selectedWouldHide()) {
    deps.apply();
    return;
  }
  deps.detail.guarded(() => {
    deps.dismiss();
    deps.apply();
  });
}
